import type { Interval } from 'luxon';
import { DateTime } from 'luxon';
import { createSelector } from 'reselect';

import type { FilterObject } from 'app-components/flux/filters/filtersTypes';
import { reportsSelectors } from 'app-components/flux/reports';
import type RootState from 'app-components/utils/redux-store/rootState';

type ConversationalCategorySelectorProps = {
  preDefinedDateRange: Interval;
  includeTransactions?: boolean;
  filterObject?: FilterObject | null;
};

type ReportTxn = {
  postedOn: string;
  payee?: string;
  cpdata?: { payee?: string };
  amount: number;
  categoryId?: string;
  coa?: { type?: string };
  id: string;
};

type ReportGroup = {
  total: number;
  txns: ReportTxn[];
};

type ReportPeriod = {
  timeStamp: string;
  total?: number;
  txns: ReportTxn[];
  groups: Record<string, ReportGroup>;
};

type CategorySummaryRow = {
  category: string;
  periodAmount: string;
};

type TransactionSummaryRow = {
  date: string;
  payee: string | undefined;
  originalStatementPayee: string | undefined;
  amount: number;
  category: string;
  categoryId: string | undefined;
  coa: ReportTxn['coa'];
  id: string;
};

type PeriodCategoryRow = {
  transactions: TransactionSummaryRow[] | false;
  category: string;
  periodAmount: string;
};

type IncomeSemantics = {
  defaultIncomeSource: 'actual_income_report';
  plannedIncomeSource: 'spending_plan_v2';
};

type ConversationalCategoryData = {
  catJSON: Record<string, unknown> & {
    overallSpending: CategorySummaryRow[];
    overallIncome: CategorySummaryRow[];
    incomeSemantics: IncomeSemantics;
  };
};

const INCOME_SEMANTICS: IncomeSemantics = Object.freeze({
  defaultIncomeSource: 'actual_income_report',
  plannedIncomeSource: 'spending_plan_v2',
});

// Keep Immutable method calls on the object. Detaching `get` to a local variable can lose `this`
// binding at runtime and throw (e.g. reading `_root` from undefined).
const getPeriodsFromReportData = (reportData: unknown): ReportPeriod[] => {
  if (!reportData || typeof reportData !== 'object') return [];
  const immutableLike = reportData as { get?: (key: string) => unknown };
  if (typeof immutableLike.get !== 'function') return [];

  const rawReport = immutableLike.get('rawReport');
  if (!rawReport || typeof rawReport !== 'object') return [];
  // Same as above: call toJS() on the object — detaching it loses `this` and returns undefined.
  const immutableReport = rawReport as { toJS?: () => unknown };
  if (typeof immutableReport.toJS !== 'function') return [];

  const rawData = immutableReport.toJS();
  if (!rawData || typeof rawData !== 'object') return [];
  const periods = (rawData as { periods?: unknown }).periods;
  return Array.isArray(periods) ? (periods as ReportPeriod[]) : [];
};

const summarizeTxn = (txn: ReportTxn, catName: string): TransactionSummaryRow | null => {
  if (txn.coa?.type !== 'CATEGORY' && txn.coa?.type !== 'UNCATEGORIZED') {
    return null;
  }
  return {
    date: DateTime.fromISO(txn.postedOn).toFormat('MM/dd/yyyy'),
    payee: txn.payee,
    originalStatementPayee: txn.cpdata?.payee,
    amount: txn.amount,
    category: catName,
    categoryId: txn.categoryId,
    coa: txn.coa,
    id: txn.id,
  };
};

const ensurePeriodRows = (catJSON: Record<string, unknown>, dateString: string): PeriodCategoryRow[] => {
  const existing = catJSON[dateString];
  if (Array.isArray(existing)) {
    return existing as PeriodCategoryRow[];
  }
  const rows: PeriodCategoryRow[] = [];
  catJSON[dateString] = rows;
  return rows;
};

export const getConversationalCategoryData = createSelector(
  (state: RootState, props: ConversationalCategorySelectorProps) =>
    reportsSelectors.getIncomeSpendingReportForDisplay(state, {
      reportName: 'all',
      timeStep: 'MONTHS',
      isOverTime: true,
      chartType: 'pie',
      groupBy: 'category',
      dateRange: props.preDefinedDateRange,
      ...props,
    }),
  // Behavior change vs old JS:
  // income is now sourced from the dedicated income report semantics
  // instead of inferring income from positive category totals in `all`.
  (state: RootState, props: ConversationalCategorySelectorProps) =>
    reportsSelectors.getIncomeSpendingReportForDisplay(state, {
      reportName: 'income',
      timeStep: 'MONTHS',
      isOverTime: true,
      chartType: 'pie',
      groupBy: 'category',
      dateRange: props.preDefinedDateRange,
      ...props,
    }),
  (_state: RootState, props: ConversationalCategorySelectorProps) => props.includeTransactions,
  (
    catChartData: unknown,
    incomeChartData: unknown,
    includeTransactions: boolean | undefined,
  ): ConversationalCategoryData | undefined => {
    if (!catChartData) return undefined;

    const catPeriods = getPeriodsFromReportData(catChartData);
    const incomePeriods = getPeriodsFromReportData(incomeChartData);
    // New behavior: period and overall income totals come from the income report.
    const incomePeriodTotalsByTimestamp = incomePeriods.reduce<Record<string, number>>((acc, period) => {
      acc[period.timeStamp] = period.total || 0;
      return acc;
    }, {});
    const incomeCategoryTotals = incomePeriods.reduce<Record<string, number>>((acc, period) => {
      Object.entries(period.groups || {}).forEach(([catName, catRecord]) => {
        const total = catRecord?.total || 0;
        acc[catName] = (acc[catName] || 0) + total;
      });
      return acc;
    }, {});

    const catSpendTotal: Record<string, number> = {};
    const catJSON: Record<string, unknown> = {};

    // if the range is going to be over 12 months, we do NOT include transactions until we can handle larger
    // frame sizes via the websocket

    const includeTxns = Boolean(includeTransactions) && catPeriods.length <= 6;

    catPeriods?.forEach((period) => {
      if (Object.keys(period.groups || {}).length > 0) {
        const dateTime = DateTime.fromISO(period.timeStamp);
        const dateString = dateTime.toFormat('MMMM, yyyy');
        let periodSpendTotal = 0;
        const periodIncomeTotal = incomePeriodTotalsByTimestamp[period.timeStamp] || 0;
        const periodRows = ensurePeriodRows(catJSON, dateString);
        Object.entries(period.groups).forEach(([catName, catRecord]) => {
          const txns = catRecord.txns
            .map((txn) => summarizeTxn(txn, catName))
            .filter((txn): txn is TransactionSummaryRow => txn !== null);

          periodRows.push({
            transactions: includeTxns && txns,
            category: catName,
            periodAmount: catRecord.total.toFixed(2),
          });
          periodSpendTotal += catRecord.total < 0 ? catRecord.total : 0;
          if (catRecord.total <= 0) {
            catSpendTotal[catName] = catSpendTotal[catName]
              ? catSpendTotal[catName] + catRecord.total
              : catRecord.total;
          }
        });

        (
          catJSON[dateString] as PeriodCategoryRow[] & {
            totalIncome?: string;
            totalExpense?: string;
          }
        ).totalIncome = periodIncomeTotal.toFixed(2);
        (
          catJSON[dateString] as PeriodCategoryRow[] & {
            totalIncome?: string;
            totalExpense?: string;
          }
        ).totalExpense = periodSpendTotal.toFixed(2);
      }
    });

    const overallSpending: CategorySummaryRow[] = [];
    const overallIncome: CategorySummaryRow[] = [];
    Object.entries(catSpendTotal)?.forEach(([catName, val]) => {
      overallSpending.push({ category: catName, periodAmount: val.toFixed(2) });
    });
    Object.entries(incomeCategoryTotals)?.forEach(([catName, val]) => {
      overallIncome.push({ category: catName, periodAmount: val.toFixed(2) });
    });
    const catJSONWithTotals = {
      ...catJSON,
      overallSpending,
      overallIncome,
      incomeSemantics: INCOME_SEMANTICS,
    };

    // console.log("JSON OBJECT IS ", JSON.stringify(catJSON));

    return {
      catJSON: catJSONWithTotals,
    };
  },
);
